<script>
import * as m from '@app/paraglide/messages.js';
import {
    showModal
} from '@app/store/modalStore.svelte.js';
import {
    eventGroupState, isSelectedEventGroupLocked
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    speakerState
} from '@app/store/speakerStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    toast } from '@zerodevx/svelte-toast';
import {
    insertHistoryAction
} from "@app/store/historyStore.svelte.js";
import { clickoutside } from '@svelte-put/clickoutside';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import { saveAs } from 'file-saver';
import {
    buildSpeakerMetrics,
    generateSpeakerMetricsDocx
} from '@app/lib/speakerMetrics.js';

// View state - 'grid' or 'table'
let viewMode = $state('grid');
let showMenu = $state(false);
let isLocked = $derived(isSelectedEventGroupLocked());

function assignSpeakerVoice(speaker) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    if (eventGroupState[projectState.selected].selected.length > 0 && eventGroupState[projectState.selected]) {
        eventGroupState[projectState.selected].selected.forEach(eventId => {
            if (eventGroupState[projectState.selected].events[eventId]) {
                eventGroupState[projectState.selected].events[eventId].speakers = [...eventGroupState[projectState.selected].events[eventId].speakers, speaker].filter((value, index, self) =>
                    index === self.findIndex((t) => (
                        t.place === value.place && t.name === value.name
                    ))
                );
            }
        });

        toast.push(m.speakers_updated_toast(), {classes: ['toast-success']});

        insertHistoryAction({
            name: "speaker update", //action name
            eventGroup: projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }, ],
        });
    }
}

function assignRecipientVoice(speaker) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    if (eventGroupState[projectState.selected].selected.length > 0 && eventGroupState[projectState.selected]) {
        eventGroupState[projectState.selected].selected.forEach(eventId => {
            if (eventGroupState[projectState.selected].events[eventId]) {
                eventGroupState[projectState.selected].events[eventId].recipients = [...eventGroupState[projectState.selected].events[eventId].recipients, speaker].filter((value, index, self) =>
                    index === self.findIndex((t) => (
                        t.place === value.place && t.name === value.name
                    ))
                );
            }
        });

        toast.push(m.speakers_recipient_updated_toast(), {classes: ['toast-success']});

        insertHistoryAction({
            name: "speaker update", //action name
            eventGroup: projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }, ],
        });
    }
}

function openSpeakerManager() {
    showMenu = false;
    showModal("speakerManager");
}

async function exportSpeakerMetrics() {
    showMenu = false;

    try {
        const metrics = buildSpeakerMetrics(eventGroupState, speakerState);
        const defaultFilename = `${projectState.name || "Untitled"}_Speaker_Metrics`;

        const res = await Swal.fire({
            ...SWAL_BASE,
            titleText: m.speakers_metrics_filename_title(),
            showCancelButton: true,
            confirmButtonText: m.speakers_metrics_export_button(),
            input: 'text',
            inputLabel: m.speakers_metrics_filename_label(),
            inputValue: defaultFilename,
            inputPlaceholder: m.speakers_metrics_filename_placeholder(),
            customClass: SWAL_CLASSES.confirmCancel,
        });

        if (!res.isConfirmed || !res.value) {
            return;
        }

        const blob = await generateSpeakerMetricsDocx({
            projectName: projectState.name,
            authorEmail: authState.user?.email || "",
            metrics,
            labels: {
                subtitle: m.speakers_metrics_subtitle(),
                authorLine: m.speakers_metrics_by_prefix({
                    email: authState.user?.email || "",
                }),
                speakerHeader: m.speakers_metrics_speaker_header(),
                eventsSuffix: (name) => m.speakers_metrics_events_suffix({ name }),
                wordsSuffix: (name) => m.speakers_metrics_words_suffix({ name }),
                totalEventsHeader: m.speakers_metrics_total_events_header(),
                totalWordsHeader: m.speakers_metrics_total_words_header(),
                totalsRow: m.speakers_metrics_totals_row(),
                unassignedRow: m.speakers_metrics_unassigned_row(),
            },
        });

        saveAs(blob, `${res.value}.docx`);
        toast.push(m.speakers_metrics_export_complete_toast(), { classes: ['toast-success'] });
    } catch (err) {
        console.error(err, "Error exporting speaker metrics");
        toast.push(m.speakers_metrics_export_failed_toast({ error: err.message }), {
            classes: ['toast-danger'],
        });
    }
}
</script>

<div id='SpeakerWrapper' class="rounded resize-vertical" role="region" aria-label={m.speakers_wrapper_aria_label()}>
    <div class="row p-3">
        <div id="SpeakerList" class="col" role="listbox" aria-label={m.speakers_list_aria_label()}>
            <!-- View Toggle Controls -->
            <div class="d-flex justify-content-end mb-2">
                <div class="btn-group btn-group-sm" role="group" aria-label={m.speakers_view_toggle_aria_label()}>
                    <button 
                        type="button" 
                        class="btn {viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}" 
                        onclick={() => viewMode = 'grid'}
                        aria-label={m.speakers_grid_view_aria_label()}
                        title={m.speakers_grid_view_title()}>
                        <i class="bi bi-grid-3x3-gap-fill" aria-hidden="true"></i>
                    </button>
                    <button 
                        type="button" 
                        class="btn {viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}" 
                        onclick={() => viewMode = 'table'}
                        aria-label={m.speakers_table_view_aria_label()}
                        title={m.speakers_table_view_title()}>
                        <i class="bi bi-list" aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            <!-- Content Container -->
            <div class="speaker-content">
                {#if viewMode === 'grid'}
                    <!-- Grid View -->
                    <div class="row g-2">
                        {#each speakerState as speaker}
                        <div class="col-3">
                            <div class="speakerCard p-2 text-center bg-light rounded" role="listitem">
                                <!-- svelte-ignore a11y_missing_content -->
                                <h1 style="color:{speaker.colour}; font-size: 2vw; margin:0;"><i class="bi bi-person-fill"></i></h1>
                                <p style="color:{speaker.colour}" class="small m-0 text-capitalize text-truncate" title='{speaker.name}'>{speaker.name}</p>
                                <div class="btn-group btn-group-sm" role="group" aria-label={m.speakers_actions_aria_label()}>
                                    <button 
                                        type="button" 
                                        class="btn btn-secondary text-custom-medium" 
                                        title={m.speakers_add_speaker_title()} 
                                        id="addSpeaker_{speaker.id}"
                                        aria-label={m.speakers_add_speaker_aria_label({speakerName: speaker.name})}
                                        disabled={isLocked}
                                        onclick={() => assignSpeakerVoice(speaker)}>
                                        <i class="bi bi-plus" aria-hidden="true"></i> SP
                                    </button>
                                    <button 
                                        type="button" 
                                        class="btn btn-secondary text-custom-medium" 
                                        title={m.speakers_add_recipient_title()} 
                                        id="addRecipient_{speaker.id}"
                                        aria-label={m.speakers_add_recipient_aria_label({speakerName: speaker.name})}
                                        disabled={isLocked}
                                        onclick={() => assignRecipientVoice(speaker)}>
                                        <i class="bi bi-plus" aria-hidden="true"></i> RE
                                    </button>
                                </div>                        
                            </div>
                        </div>
                        {:else}
                        <p class="m-3 text-muted" role="alert">{m.speakers_no_speakers_found()}</p>
                        {/each}
                    </div>
                {:else}
                    <!-- Table View -->
                     <div  class="table-responsive">
                    <table class="table table-hover table-sm mb-0">
                        <thead class="sticky-top bg-white">
                            <tr>
                                <th scope="col" class="text-center" style="width: 50px;">{m.speakers_table_icon_header()}</th>
                                <th scope="col">{m.speakers_table_name_header()}</th>
                                <th scope="col" class="text-center" style="width: 120px;">{m.speakers_table_actions_header()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each speakerState as speaker}
                            <tr class="speakerRow">
                                <td class="text-center align-middle">
                                    <i class="bi bi-person-fill" style="color:{speaker.colour}; font-size: 1.5rem;"></i>
                                </td>
                                <td class="align-middle">
                                    <span style="color:{speaker.colour}" class="text-capitalize">{speaker.name}</span>
                                </td>
                                <td class="text-center align-middle">
                                    <div class="btn-group btn-group-sm action-buttons" role="group" aria-label={m.speakers_actions_aria_label()}>
                                        <button 
                                            type="button" 
                                            class="btn btn-outline-primary btn-sm" 
                                            title={m.speakers_add_speaker_title()} 
                                            id="addSpeaker_table_{speaker.id}"
                                            aria-label={m.speakers_add_speaker_aria_label({speakerName: speaker.name})}
                                            disabled={isLocked}
                                            onclick={() => assignSpeakerVoice(speaker)}>
                                            <i class="bi bi-plus" aria-hidden="true"></i> SP
                                        </button>
                                        <button 
                                            type="button" 
                                            class="btn btn-outline-primary btn-sm" 
                                            title={m.speakers_add_recipient_title()} 
                                            id="addRecipient_table_{speaker.id}"
                                            aria-label={m.speakers_add_recipient_aria_label({speakerName: speaker.name})}
                                            disabled={isLocked}
                                            onclick={() => assignRecipientVoice(speaker)}>
                                            <i class="bi bi-plus" aria-hidden="true"></i> RE
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            {:else}
                            <tr>
                                <td colspan="3" class="text-center text-muted p-3">{m.speakers_no_speakers_found()}</td>
                            </tr>
                            {/each}
                        </tbody>
                    </table>
                    </div>
                {/if}
            </div>
        </div>
        <div class="col-auto">
            <div class="dropdown float-end overflow-x-visible" use:clickoutside onclickoutside={() => showMenu = false}>
                <button
                    type="button"
                    class="btn btn-light btn-sm"
                    id="manageSpeakers"
                    aria-label={m.speakers_menu_label()}
                    onclick={() => showMenu = true}>
                    <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
                </button>
                <ul class="dropdown-menu {showMenu ? 'show' : ''}">
                    <li>
                        <a class="dropdown-item" href="#!/" onclick={openSpeakerManager}>
                            <i class="bi bi-people" aria-hidden="true"></i> {m.speakers_manage_menu()}
                        </a>
                    </li>
                    <li>
                        <a class="dropdown-item" href="#!/" onclick={exportSpeakerMetrics}>
                            <i class="bi bi-filetype-docx" aria-hidden="true"></i> {m.speakers_export_metrics_menu()}
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</div>

<style lang="css">

#SpeakerWrapper {
    background-color: rgba(255, 255, 255, 0.03);
    overflow-x: hidden;
    height : 140px;
}

/* Grid view styles */
.speakerCard .btn-group {
    display : none;
}

.speakerCard:hover .btn-group {
    display : block;
}

/* Table view styles */
.speakerRow .action-buttons {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.speakerRow:hover .action-buttons {
    opacity: 1;
}

.table {
    margin-bottom: 0;
}

/* View toggle button styling */
.btn-group .btn {
    transition: all 0.2s ease;
}

.btn-group .btn i {
    font-size: 1rem;
}

.dropdown-menu {
    position: absolute;
    right: 0px;
    top: 35px;
}
</style>